import { useEffect, useRef } from 'react';
import type { Clip, MediaAsset } from '@cinegrid/core';
import { resolveAssetUrl } from '../../media/mediaStore';
import { filmstripTimes, getFilmstripFrames } from '../../media/thumbnailCache';
import { drawWaveform, getWaveformPeaks, waveformStyleFromAudio } from '../../media/waveformCache';

/** Video filmstrip (or still image) inside a timeline clip. */
export function ClipFilmstrip({
  clip,
  asset,
  widthPx,
}: {
  clip: Clip;
  asset: MediaAsset;
  widthPx: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let cancelled = false;
    const canvas = canvasRef.current;
    if (!canvas || widthPx < 8) return;

    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const cssH = 44;
    canvas.width = Math.max(1, Math.floor(widthPx * dpr));
    canvas.height = Math.floor(cssH * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const paintTiles = (frames: (string | null)[]) => {
      if (cancelled) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const tileW = canvas.width / Math.max(1, frames.length);
      frames.forEach((src, i) => {
        if (!src) return;
        const img = new Image();
        img.onload = () => {
          if (cancelled) return;
          const scale = Math.max(tileW / img.width, canvas.height / img.height);
          const dw = img.width * scale;
          const dh = img.height * scale;
          const dx = i * tileW + (tileW - dw) / 2;
          const dy = (canvas.height - dh) / 2;
          ctx.drawImage(img, dx, dy, dw, dh);
          ctx.fillStyle = 'rgba(0,0,0,0.35)';
          ctx.fillRect((i + 1) * tileW - dpr, 0, dpr, canvas.height);
        };
        img.src = src;
      });
    };

    if (asset.kind === 'image') {
      void resolveAssetUrl(asset).then((url) => {
        if (cancelled) return;
        const count = Math.max(1, Math.ceil(widthPx / 52));
        paintTiles(Array.from({ length: count }, () => url));
      });
      return () => {
        cancelled = true;
      };
    }

    const times = filmstripTimes(clip.inPoint, clip.duration, widthPx, 52);
    void getFilmstripFrames(asset, times).then(paintTiles);

    return () => {
      cancelled = true;
    };
  }, [asset, clip.inPoint, clip.duration, widthPx]);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute inset-0 h-full w-full opacity-90"
    />
  );
}

/** Audio waveform inside a timeline clip. */
export function ClipWaveform({
  clip,
  asset,
  widthPx,
}: {
  clip: Clip;
  asset: MediaAsset;
  widthPx: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audio = clip.properties.audio;
  const opacity = clip.properties.transform.opacity ?? 1;

  useEffect(() => {
    let cancelled = false;
    const canvas = canvasRef.current;
    if (!canvas || widthPx < 8) return;

    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const cssH = 32;
    canvas.width = Math.max(1, Math.floor(widthPx * dpr));
    canvas.height = Math.floor(cssH * dpr);

    const style = waveformStyleFromAudio(audio, opacity);

    void getWaveformPeaks(asset).then((data) => {
      if (cancelled || !data) return;
      const accent =
        getComputedStyle(document.documentElement).getPropertyValue('--cg-accent').trim() ||
        '#e8a33d';
      drawWaveform(canvas, data, clip.inPoint, clip.duration, accent, style);
    });

    return () => {
      cancelled = true;
    };
  }, [
    asset,
    clip.inPoint,
    clip.duration,
    widthPx,
    opacity,
    audio?.volume,
    audio?.noiseRemoval?.enabled,
    audio?.noiseRemoval?.amount,
    audio?.eq?.enabled,
    audio?.eq?.low,
    audio?.eq?.mid,
    audio?.eq?.high,
    audio?.compressor?.enabled,
    audio?.compressor?.threshold,
    audio?.compressor?.ratio,
    audio?.reverb?.enabled,
    audio?.reverb?.amount,
  ]);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute inset-0 h-full w-full opacity-85"
    />
  );
}
